import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BookingRequestDto } from '@wingseat/shared';
import { api } from '../lib/api-client';
import { flightKeys } from './use-flights';

const bookingKeys = {
  all: ['booking-requests'] as const,
  mine: ['booking-requests', 'mine'] as const,
  forMyFlights: ['booking-requests', 'for-my-flights'] as const,
};

export function useMyBookingRequests() {
  return useQuery({
    queryKey: bookingKeys.mine,
    queryFn: () => api.get<BookingRequestDto[]>('/booking-requests/mine'),
  });
}

export function useBookingRequestsForMyFlights() {
  return useQuery({
    queryKey: bookingKeys.forMyFlights,
    queryFn: () =>
      api.get<BookingRequestDto[]>('/booking-requests/for-my-flights'),
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  qc.invalidateQueries({ queryKey: bookingKeys.all });
  qc.invalidateQueries({ queryKey: flightKeys.all });
}

export function useConfirmBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string | null }) =>
      api.post<BookingRequestDto>(`/booking-requests/${vars.id}/confirm`, {
        decisionReason: vars.reason ?? null,
      }),
    onSuccess: () => invalidate(qc),
  });
}

export function useRejectBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string | null }) =>
      api.post<BookingRequestDto>(`/booking-requests/${vars.id}/reject`, {
        decisionReason: vars.reason ?? null,
      }),
    onSuccess: () => invalidate(qc),
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<BookingRequestDto>(`/booking-requests/${id}/cancel`),
    onSuccess: () => invalidate(qc),
  });
}
